import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import { useAppSelector } from 'store/hooks';
import { ActivityType } from 'library/types';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { CreateActivityTypeDialog, UpdateActivityTypeDialog } from './components';

const ActivityTypes = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [openDeleteActivityType, setOpenDeleteActivityType] = useState(false);
  const [openCreateActivityType, setOpenCreateActivityType] = useState(false);
  const [openUpdateActivityType, setOpenUpdateActivityType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'activityTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
      {
        field: 'roles',
        headerName: intl.formatMessage({ id: 'activityTypes.roles', defaultMessage: 'Roller' }),
        flex: 1,
        valueGetter: (value: { id: number; title: string }[]) => (value ?? []).map((role) => role.title).join(', '),
      },
      {
        field: 'isEffortActivity',
        headerName: intl.formatMessage({ id: 'activityTypes.isEffortActivity', defaultMessage: 'Efor Aktivitesi mi?' }),
        type: 'boolean',
        flex: 1,
      },
      {
        field: 'isPermissionActivity',
        headerName: intl.formatMessage({
          id: 'activityTypes.isPermissionActivity',
          defaultMessage: 'İzin Aktivitesi mi?',
        }),
        type: 'boolean',
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const getActivityTypes = async () => {
    try {
      setLoading(true);

      const response = await apiService.getActivityTypes();

      if (response.isSuccess) {
        setActivityTypes(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    getActivityTypes();
  };

  // sync helper functions
  const handleCloseDeleteActivityType = () => setOpenDeleteActivityType(false);
  const handleClickOpenDeleteActivityType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteActivityType(true);
  };

  const handleCloseCreateActivityType = () => setOpenCreateActivityType(false);
  const handleClickOpenCreateActivityType = () => setOpenCreateActivityType(true);

  const handleCloseUpdateActivityType = () => setOpenUpdateActivityType(false);
  const handleClickOpenUpdateActivityType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateActivityType(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-activity-type')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getActivityTypes();
  }, []);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-activity-type']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCreateActivityType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="activityTypes.createActivityType" defaultMessage="Aktivite Türü Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-activity-type']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenUpdateActivityType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="activityTypes.updateActivityType" defaultMessage="Aktivite Türü Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-activity-type']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenDeleteActivityType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="activityTypes.deleteActivityType" defaultMessage="Aktivite Türü Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateActivityTypeDialog
        open={openCreateActivityType}
        handleClose={handleCloseCreateActivityType}
        refresh={refresh}
      />
      <UpdateActivityTypeDialog
        open={openUpdateActivityType}
        activityTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateActivityType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
      />
      <DeleteDialog
        open={openDeleteActivityType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteActivityType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="activityTypes.deleteActivityType" defaultMessage="Aktivite Türü Sil" />}
        subTitle={
          <FormattedMessage
            id="activityTypes.deleteActivityTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra aktivite türü veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteActivityType}
        successMessage={
          <FormattedMessage
            id="activityTypes.deleteActivityTypeCompleted"
            defaultMessage="Seçili aktivite türünü silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={activityTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default ActivityTypes;
